import { Box, ButtonProps, useMediaQuery, useTheme } from "@mui/material";
import { GridAddIcon, GridDeleteIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import TruncatedText from "../../components/common/TruncatedText";
import Actions from "../../components/common/Actions";
import CustomDateTimePicker from "../../components/common/CustomDateTimePicker";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ToastNotification from "../../components/ToastNotification";
import config from "../../config.json";
import {
  addServiceProviderCostApi,
  ServiceProviderCostBulkApi,
  ServiceProviderCostHistory,
  ServiceProviderCostType,
  deleteServiceProviderCostApi,
  editServiceProviderCostApi,
  exportServiceProviderCost,
  fetchServiceProviderCostHistory,
  useServiceProviderCostQuery,
  useServiceProviderNoDataQuery,
} from "../../services/ServiceProviderCostsService";
import {
  fetchServiceProviders,
  getServiceProviderById,
} from "../../services/ServiceProviderService";
import { fetchCountry } from "../../services/CountryService";
import { fetchCurrency } from "../../services/CurrencyService";
import { fetchOperator } from "../../services/OperatorsService";
import { fetchProfile } from "../../services/ProfilesService";
import {
  ErrorResponse,
  IField,
  Option,
  Params,
} from "../../types/table/interfaces.type";
import {
  loadCountriesOptions,
  loadOperatorsByCountry,
} from "../../utils/commonDataUtils";
import History from "../../components/History";
import AutoComplete from "../../components/common/AutoComplete";
import { IAutoCompleteProps } from "../../components/common/AutoComplete";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const ServiceProviderCostPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [countriesOptions, setCountriesOptions] = useState<Option[]>([]);
  const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
  const [serviceProvidersOptions, setServiceProvidersOptions] = useState<Option[]>([]);
  const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
  const [filters, setFilters] = useState<
    Params<ServiceProviderCostType & { countryId: number }>
  >({});
  const [appliedFilters, setAppliedFilters] = useState<Params<ServiceProviderCostType>>(
    {}
  );
  const [showTable, setShowTable] = useState(false);

  const dialogRef = useRef<ServiceProviderCostDialogRef>(null);

  const handleOpenDialog = (
    mode: "add" | "delete" | "history" | "edit",
    id?: string
  ) => {
    dialogRef.current?.openDialog(mode, id);
  };

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [filters]);

  const onRowSelect = (row: ServiceProviderCostType, checked: boolean) => {
    setSelectedRowIds((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, String(row.id)]
        : prevSelected.filter((id) => id !== String(row.id));
      console.log("Selected row IDs:", newSelected);
      return newSelected;
    });
  };

  const isRowSelected = (row: ServiceProviderCostType) =>
    selectedRowIds.includes(String(row.id));

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const countriesAsOptions = await loadCountriesOptions();
        setCountriesOptions(countriesAsOptions);

        const providerResponse = await fetchServiceProviders({
          size: config.MAX_PAGE_SIZE,
          isDeleted: false,
          enabled: true,
        });
        const options = providerResponse.content.map((sp) => ({
          label: sp.name,
          value: sp.id,
        }));
        setServiceProvidersOptions(options);
      } catch (error: any) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
  }, []);

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters({ ...filters });
    setShowTable(true);
    setSelectedRowIds([]);
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_SERVICE_PROVIDER_COST"),
    },
    onViewHistory: {
      // isDisabled: true,
      handler: async (id: number | string) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_SERVICE_PROVIDER_COSTS_HISTORY"),
    },
  });

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Service Provider",
      value: filters.serviceProviderId ?? "",
      options: serviceProvidersOptions,
      clearable: true,
      placeholder: "Select Service Provider",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProvider = serviceProvidersOptions.find(
          (option) => option.value === value
        );

        setFilters((prev) => ({
          ...prev,
          serviceProviderId: String(selectedProvider?.value ?? ""),
        }));

        console.log("Selected providerId:", selectedProvider?.label ?? "");
      },
    },
    {
      label: "Country",
      value: filters.countryId ?? "",
      options: countriesOptions,
      clearable: true,
      placeholder: "Select Country",
      onChange: async (value: string | number | (string | number)[]) => {
        let selectedCountry = countriesOptions.find(
          (option) => option.value === value
        );

        setIsOperatorDisabled(true);

        if (value === "" || value === null || value === undefined) {
          setFilters((prev) => ({
            ...prev,
            countryId: undefined,
            operatorId: undefined,
          }));
          setOperatorsByCountry([]);
          return;
        }

        try {
          const countryId = Number(selectedCountry?.value);
          if (isNaN(countryId)) {
            throw new Error("Invalid country ID");
          }

          const operators = await loadOperatorsByCountry(countryId);
          setOperatorsByCountry(operators);
          setFilters((prev) => ({
            ...prev,
            countryId: countryId,
            operatorId: undefined,
          }));

          console.log("Selected country:", selectedCountry?.label ?? "");
        } catch (error: any) {
          console.error("Error in country change:", error);
          setOperatorsByCountry([]);
          ToastNotification.error(
            "Error loading operators for selected country"
          );
        } finally {
          setIsOperatorDisabled(false);
        }
      },
    },

    {
      label: "Operator",
      value: filters.operatorId ?? "",
      options: operatorsByCountry,
      clearable: true,
      disabled: isOperatorDisabled,

      placeholder: "Select Operator",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = operatorsByCountry.find(
          (option) => option.value === value
        );
        setFilters((prev) => ({
          ...prev,
          operatorId:
            value === undefined || value === null || value === ""
              ? undefined
              : Number(selectedOperator?.value),
        }));
        console.log("Selected operator:", selectedOperator?.label ?? "");
      },
    },
  ];

  const isSearchDisabled = !Object.values(filters).some((value) => {
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    return value !== undefined && value !== null;
  });

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      disabled: isSearchDisabled || !hasJwtPermission("GET_ALL_SERVICE_PROVIDER_COSTS"),
    },
  ];

  const fields = useMemo<IField<ServiceProviderCostType>[]>(() => [
    {
      key: "serviceProviderId",
      header: "Service Provider",
      transform: async (t) => {
        if (!t.serviceProviderId) return "-";
        try {
          const sp = await queryClient.fetchQuery({
            queryKey: ["serviceProvider", t.serviceProviderId],
            queryFn: () => getServiceProviderById(t.serviceProviderId),
          });
          return sp?.name ?? "-";
        } catch {
          return "-";
        }
      },
    },
    { key: "type", header: "Type" },
    {
      key: "operatorId",
      header: "Country",
      transform: async (t) => {
        if (!t.operatorId) return "-";
        try {
          const operator = await queryClient.fetchQuery({
            queryKey: ["operator", t.operatorId],
            queryFn: () => fetchOperator(t.operatorId),
          });
          if (!operator?.countryId) return "-";
          const country = await queryClient.fetchQuery({
            queryKey: ["country", operator.countryId],
            queryFn: () => fetchCountry(operator.countryId),
          });
          return country?.name ?? "-";
        } catch {
          return "-";
        }
      },
    },
    {
      key: "operatorId",
      header: "Operator",
      transform: async (t) => {
        if (!t.operatorId) return "-";
        try {
          const operator = await queryClient.fetchQuery({
            queryKey: ["operator", t.operatorId],
            queryFn: () => fetchOperator(t.operatorId),
          });
          return operator?.name ?? "-";
        } catch {
          return "-";
        }
      },
    },
    {
      key: "operatorId",
      header: "CC",
      transform: async (t) => {
        if (!t.operatorId) return "-";
        try {
          const operator = await queryClient.fetchQuery({
            queryKey: ["operator", t.operatorId],
            queryFn: () => fetchOperator(t.operatorId),
          });
          return operator?.cc ?? "-";
        } catch {
          return "-";
        }
      },
    },
    {
      key: "operatorId",
      header: "MCC",
      transform: async (t) => {
        if (!t.operatorId) return "-";
        try {
          const operator = await queryClient.fetchQuery({
            queryKey: ["operator", t.operatorId],
            queryFn: () => fetchOperator(t.operatorId),
          });
          return operator?.mcc?.join(",") ?? "-";
        } catch {
          return "-";
        }
      },
    },
    {
      key: "operatorId",
      header: "MNC",
      transform: async (t) => {
        if (!t.operatorId) return "-";
        try {
          const operator = await queryClient.fetchQuery({
            queryKey: ["operator", t.operatorId],
            queryFn: () => fetchOperator(t.operatorId),
          });
          return <TruncatedText text={operator?.mnc?.join(",")} />;
        } catch {
          return "-";
        }
      },
    },
    { key: "cost", header: "Cost" },
    {
      key: "serviceProviderId",
      header: "Currency",
      transform: async (t) => {
        if (!t.serviceProviderId) return "-";
        try {
          const sp = await queryClient.fetchQuery({
            queryKey: ["serviceProvider", t.serviceProviderId],
            queryFn: () => getServiceProviderById(t.serviceProviderId),
          });
          if (!sp.profileId) return "-";
          const profile = await queryClient.fetchQuery({
            queryKey: ["profile", sp.profileId],
            queryFn: () => fetchProfile(sp.profileId),
          });
          const currency = await queryClient.fetchQuery({
            queryKey: ["currency", profile.currencyId],
            queryFn: () => fetchCurrency(Number(profile.currencyId)),
          });
          return currency?.name ?? "-";
        } catch {
          return "-";
        }
      },
    },
    { key: "newCost", header: "New Cost" },
    { key: "effectiveDate", header: "Effective Date" },
  ], [queryClient]);

  const handleCountryChange = async (
    value: string | number | (string | number)[]
  ) => {
    const selectedCountry = Array.isArray(value) ? value[0] : value;

    if (selectedCountry) {
      try {
        const countryId = Number(selectedCountry);
        if (isNaN(countryId)) {
          throw new Error("Invalid country ID");
        }

        setIsOperatorDisabled(true);
        const operators = await loadOperatorsByCountry(countryId);
        setOperatorsByCountry(operators);
      } catch (error: any) {
        console.error("Error fetching operators for selected country:", error);
        setOperatorsByCountry([]);
      } finally {
        setIsOperatorDisabled(false);
      }
    } else {
      setOperatorsByCountry([]);
      setIsOperatorDisabled(false);
    }
  };

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Service Provider Cost"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginTop={1}
        marginBottom={1}
      />
      <Box sx={{ mb: 3 }}>
        <SearchForm autoCompletes={autoCompletes} buttons={buttons} />
        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}></Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Service Provider Cost List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={filters.search ?? ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_SERVICE_PROVIDER_COST",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              variant: "outlined" as ButtonProps["variant"],
              startIcon: <GridDeleteIcon />,
              onClick: () => handleOpenDialog("delete"),
              textColor: COLORS.iconColor,
              permission: "DELETE_SERVICE_PROVIDER_COST",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: async () => {
                try {
                  await exportServiceProviderCost(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again."
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_SERVICE_PROVIDER_COSTS_CSV",
            },
          ]}
        />

        <CustomTable<ServiceProviderCostType>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={showTable ? useServiceProviderCostQuery : useServiceProviderNoDataQuery}
          actions={actions}
          checkboxSelect={true}
          onRowSelect={onRowSelect}
          isRowSelected={isRowSelected}
          sortableFields={["cost", "effectiveDate"]}
        />
        {!showTable && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="120px"
          >
            <CustomLabel
              text="Please use the search above to display service provider cost data."
              variant="body1"
              fontWeight={400}
              color={COLORS.placeHolderColor}
              marginTop={2}
              marginBottom={2}
            />
          </Box>
        )}
      </Box>
      <ServiceProviderCostDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        selectedRowIds={selectedRowIds}
        onClearSelection={() => setSelectedRowIds([])}
        serviceProvidersOptions={serviceProvidersOptions}
        countriesOptions={countriesOptions}
        appliedFilters={appliedFilters}
        loadOperatorsByCountry={loadOperatorsByCountry}
      />
    </Box>
  );
};

interface DialogData extends Omit<ServiceProviderCostType, "operatorId"> {
  countryId: number;
  operatorId?: number | number[];
  currencyName: string;
  effectiveDate: Date;
  name: string;
}
export interface ServiceProviderCostDialogRef {
  openDialog: (
    mode: "add" | "delete" | "history" | "edit",
    id?: string
  ) => void;
}

interface ServiceProviderCostDialogProps {
  selectedRowIds: string[];
  onRefresh: () => void;
  onClearSelection: () => void;
  countriesOptions: Option[];
  appliedFilters: Params<ServiceProviderCostType>;

  serviceProvidersOptions: Option[];
  loadOperatorsByCountry: (countryId: number) => Promise<Option[]>;
}

const ServiceProviderCostDialog = forwardRef<ServiceProviderCostDialogRef, ServiceProviderCostDialogProps>(
  (
    {
      onRefresh,
      countriesOptions,
      loadOperatorsByCountry,
      serviceProvidersOptions,

      appliedFilters,
      selectedRowIds,
      onClearSelection,
    },
    ref
  ) => {
    const queryClient = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "delete" | "history" | "edit"
    >("add");
    const [dialogData, setDialogData] = useState<Partial<DialogData>>({});

    const [selectedId, setSelectedId] = useState<string | undefined>();

    const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
    const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id) => {
        if (mode === "history") {
          setSelectedId(id);
          setDialogOpen(true);
        }
        setDialogMode(mode);
        setSelectedId(id);
        openDialog(mode, id);
      },
    }));

    const handleCountryChange = async (
      value: string | number | (string | number)[]
    ) => {
      const selectedCountry = Array.isArray(value) ? value[0] : value;
      setDialogData((prev) => ({
        ...prev,
        countryId: Number(selectedCountry),
        operatorId: undefined,
      }));

      if (selectedCountry) {
        try {
          const countryId = Number(selectedCountry);
          if (isNaN(countryId)) {
            throw new Error("Invalid country ID");
          }

          setIsOperatorDisabled(false);
          const operators = await loadOperatorsByCountry(countryId);
          setOperatorsByCountry(operators);
        } catch (error: any) {
          ToastNotification.error(
            "Error fetching operators for selected country."
          );
          setOperatorsByCountry([]);
        }
      } else {
        setOperatorsByCountry([]);
        setIsOperatorDisabled(true);
      }
    };

    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };
    useEffect(() => {
      const isAnyFieldEmpty =
        !dialogData.cost ||
        !dialogData.serviceProviderId ||
        !dialogData.operatorId;
      setIsSaveDisabled(isAnyFieldEmpty);
    }, [dialogData]);

    const openDialog = async (
      mode: "add" | "delete" | "edit" | "history",
      id?: string
    ) => {
      setDialogMode(mode);

      try {
        if (mode === "add") {
          setDialogData({
            serviceProviderId: "",
            operatorId: [],
            cost: undefined,
            comment: "",
            currencyName: "",
            type: undefined,
          });
          setDialogOpen(true);
        } else if (mode === "edit" && id) {
          const costData = await editServiceProviderCostApi(id);
          setDialogData({
            ...costData,
            operatorId: costData.operatorId
              ? [costData.operatorId]
              : [],
          });

          if (costData.operatorId) {
            const { countryId: selectedCountry } = await fetchOperator(
              costData.operatorId
            );

            if (selectedCountry) {
              const countryId = Number(selectedCountry);
              setIsOperatorDisabled(true);
              const ops = await loadOperatorsByCountry(countryId);
              setOperatorsByCountry(ops);
              setDialogData((prev) => ({
                ...prev,
                countryId: countryId,
                operatorId: Array.isArray(costData.operatorId)
                  ? costData.operatorId
                  : [costData.operatorId],
              }));
              setIsOperatorDisabled(false);
            }
          }

          if (costData.serviceProviderId) {
            try {
              const provider = await getServiceProviderById(
                costData.serviceProviderId
              );
              if (provider.profileId) {
                const profile = await fetchProfile(provider.profileId);
                const currency = await fetchCurrency(profile.currencyId);
                setDialogData((prev) => ({
                  ...prev,
                  currencyName: currency.name,
                  type: provider.type,
                }));
              } else {
                setDialogData((prev) => ({
                  ...prev,
                  type: provider.type,
                }));
              }
            } catch (error: any) {
              console.error(
                "Error fetching currency for selected provider:",
                error
              );
              ToastNotification.error(error.message);
              setDialogData((prev) => ({ ...prev, currencyName: "" }));
            }
          }

          setDialogOpen(true);
        } else if (mode === "delete") {
          setDialogOpen(true);
        }
      } catch (error: any) {
        console.error(`Error fetching cost data for ${mode} mode:`, error);
      }
    };

    const closeDialog = () => {
      setDialogOpen(false);
      setIsOperatorDisabled(true);
      setDialogData({});
    };

    const addServiceProviderCost = async (data: Partial<DialogData>) => {
      try {
        const { operatorId, type, ...restData } = data;
        const commonData: Partial<ServiceProviderCostType> = {
          ...restData,
          isDeleted: false,
        };

        if (Array.isArray(operatorId) && operatorId.length > 1) {
          const payloads: Partial<ServiceProviderCostType>[] = operatorId.map(
            (id, index) => ({
              ...commonData,
              operatorId: id,
              name: data.name ? `${data.name}_${index + 1}` : data.name,
            })
          );
          await ServiceProviderCostBulkApi(payloads);
        } else {
          const payload: Partial<ServiceProviderCostType> = {
            ...commonData,
            operatorId: Array.isArray(operatorId) ? operatorId[0] : operatorId,
          };
          await addServiceProviderCostApi(payload);
        }

        onRefresh();
        closeDialog();
        setDialogData({ effectiveDate: new Date() });
        ToastNotification.success("Cost added successfully!");
      } catch (error: any) {
        console.error("Error adding service provider cost:", error);
        const typedError = error as ErrorResponse;
        const errorMessage =
          typedError.response?.data?.message ??
          typedError.message ??
          "An unknown error occurred while adding the service provider cost.";
        ToastNotification.error(errorMessage, { timeOut: 3000 });
      }
    };

    const deleteServiceProviderCost = async () => {
      try {
        await deleteServiceProviderCostApi(selectedRowIds);
        ToastNotification.success("Cost deleted successfully!");
        onClearSelection();
        onRefresh();
        setDialogOpen(false);
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const fieldHistory = useMemo<IField<ServiceProviderCostType & ServiceProviderCostHistory>[]>(() => [
      {
        key: "serviceProviderId",
        header: "Service Provider",
        transform: async (t) => {
          if (!t.serviceProviderId) return "-";
          try {
            const sp = await queryClient.fetchQuery({
              queryKey: ["serviceProvider", t.serviceProviderId],
              queryFn: () => getServiceProviderById(t.serviceProviderId),
            });
            return sp?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      { key: "type", header: "Type" },
      {
        key: "operatorId",
        header: "Country",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            if (!operator?.countryId) return "-";
            const country = await queryClient.fetchQuery({
              queryKey: ["country", operator.countryId],
              queryFn: () => fetchCountry(operator.countryId),
            });
            return country?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Operator",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return operator?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "CC",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return operator?.cc ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "MCC",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return operator?.mcc?.join(",") ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "MNC",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return <TruncatedText text={operator?.mnc?.join(",")} />;
          } catch {
            return "-";
          }
        },
      },
      { key: "cost", header: "Cost" },
      { key: "newCost", header: "New Cost" },
      { key: "effectiveDate", header: "Effective Date" },
      { key: "historyTimestamp", header: "Date Modified" },
      { key: "createdBy", header: "Created By" },
      { key: "updatedBy", header: "Updated By" },
    ], [queryClient]);

    return (
      <CustomDialog
        open={dialogOpen}
        onClose={closeDialog}
        mode={dialogMode}
        title={
          dialogMode === "edit"
            ? "Edit Cost"
            : dialogMode === "history"
            ? "History"
            : dialogMode === "add"
            ? "Add New Cost "
            : "Delete Cost"
        }
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History
                      filters={appliedFilters}
                      fields={fieldHistory}
                      fetchFunc={() => fetchServiceProviderCostHistory(String(selectedId))}
                    />
                  ),
                },
              ]
            : dialogMode === "delete"
            ? [
                {
                  type: "custom",
                  component: (
                    <Box>
                      <p>Are you sure you want to delete this route?</p>
                    </Box>
                  ),
                },
              ]
            : [
                {
                  type: "custom",
                  component: (
                    <AutoComplete
                      label="Service Provider"
                      highlightLabelStar={true}
                      value={dialogData.serviceProviderId || ""}
                      onChange={async (
                        value: string | number | (string | number)[]
                      ) => {
                        const selectedProviderId =
                          value === undefined || value === null || value === ""
                            ? ""
                            : String(value);
                        setDialogData((prev) => ({
                          ...prev,
                          serviceProviderId: selectedProviderId,
                        }));
                        validateField(
                          "serviceProviderId",
                          selectedProviderId
                        );

                        if (!selectedProviderId) {
                          setDialogData((prev) => ({
                            ...prev,
                            currencyName: "",
                            type: undefined,
                          }));
                          return;
                        }

                        try {
                          const provider = await getServiceProviderById(selectedProviderId);
                          if (provider.profileId) {
                            const profile = await fetchProfile(provider.profileId);
                            const currency = await fetchCurrency(profile.currencyId);
                            setDialogData((prev) => ({
                              ...prev,
                              currencyName: currency.name,
                              type: provider.type,
                            }));
                          } else {
                            setDialogData((prev) => ({
                              ...prev,
                              type: provider.type,
                            }));
                          }
                        } catch (error: any) {
                          console.error(
                            "Error fetching currency for selected provider:",
                            error
                          );
                          ToastNotification.error(error.message);
                          setDialogData((prev) => ({
                            ...prev,
                            currencyName: "",
                          }));
                        }
                      }}
                      options={serviceProvidersOptions}
                      error={!!errors.serviceProviderId}
                      helperText={errors.serviceProviderId
                        ? "Service Provider is required"
                        : ""}
                      placeholder="Select Service Provider"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: COLORS.WhiteBackground,
                        },
                      }}
                    />
                  ),
                },
                {
                  type: "text",
                  label: "Type",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: false,
                  value: dialogData.type ?? "",
                  disabled: true,
                  onChange: (value) => {},
                  placeholder: "Type",
                },
                {
                  type: "custom",
                  component: (
                    <AutoComplete
                      label="Country"
                      highlightLabelStar={true}
                      value={dialogData.countryId || ""}
                      onChange={async (
                        value: string | number | (string | number)[]
                      ) => {
                        const selectedCountryId = Number(value);
                        if (isNaN(selectedCountryId)) {
                          console.error("Invalid country ID");
                          return;
                        }

                        setDialogData((prev) => ({
                          ...prev,
                          countryId: Number(value),
                          operatorId: undefined,
                        }));

                        await loadOperatorsByCountry(selectedCountryId);

                        try {
                          handleCountryChange(value);
                        } catch (error: any) {
                          console.error("Error in handleCountryChange:", error);
                        }
                      }}
                      options={countriesOptions}
                      error={!!errors.country}
                      helperText={errors.country ? "Country is required" : ""}
                      placeholder="Select Country"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: COLORS.WhiteBackground,
                        },
                      }}
                    />
                  ),
                },

                {
                  type: "custom",
                  component: (
                    <AutoComplete
                      label="Operator"
                      multiple={true}
                      selectAll={true}
                      hideChips={true}
                      value={
                        Array.isArray(dialogData.operatorId)
                          ? dialogData.operatorId.map(String)
                          : []
                      }
                      options={operatorsByCountry}
                      onChange={(
                        value: string | number | (string | number)[]
                      ) => {
                        const valueArray: number[] = Array.isArray(value)
                          ? value.map((v) => Number(v))
                          : value
                          ? [Number(value)]
                          : [];

                        console.log("Updated operatorId:", valueArray);

                        setDialogData((prev) => ({
                          ...prev,
                          operatorId: valueArray,
                        }));
                      }}
                      clearable={true}
                      disablePortal={false}
                      error={!!errors.operatorId}
                      placeholder="Select Operator"
                      highlightLabelStar={true}
                      helperText={
                        errors.operatorId ? "Operator is required" : ""
                      }
                      disabled={isOperatorDisabled}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: COLORS.WhiteBackground,
                        },
                      }}
                    />
                  ),
                },
                {
                  type: "text",
                  typeinput: "number",
                  label: "Cost",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.cost !== undefined ? dialogData.cost : "",
                  onChange: (value) => {
                    const numericValue =
                      value === "" ? undefined : Number(value);
                    setDialogData((prev) => ({
                      ...prev,
                      cost: numericValue,
                    }));
                  },

                  placeholder: "Enter Cost",
                  error: !!errors.cost,
                  helperText: errors.cost ? "Cost is required" : "",
                },
                {
                  type: "text",
                  label: "Currency",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: false,
                  value: dialogData.currencyName ?? "",
                  disabled: true,
                  onChange: (value) => {},
                  placeholder: "Existing Currency",
                },

                {
                  type: "custom",
                  highlightLabelStar: true,
                  component: (
                    <CustomDateTimePicker
                      label="Effective Date"
                      value={
                        dialogData.effectiveDate
                          ? new Date(dialogData.effectiveDate)
                          : null
                      }
                      onChange={(date: Date | null) => {
                        setDialogData((prev) => ({
                          ...prev,
                          effectiveDate: date || undefined,
                        }));
                      }}
                      error={!!errors.effectiveDate}
                      helperText={
                        errors.effectiveDate ? "Effective Date is required" : ""
                      }
                    />
                  ),
                },
                {
                  type: "text",
                  label: "Comment",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: false,
                  value: dialogData.comment ?? "",
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      comment: String(value),
                    }));
                  },
                  placeholder: "Enter comment",
                  error: !!errors.comment,
                  helperText: errors.comment ? "comment is required" : "",
                },
              ]
        }
        buttons={
          dialogMode === "history"
            ? []
            : [
                dialogMode !== "delete"
                  ? {
                      label: "Save",
                      variant: "contained",
                      bgColor: COLORS.GOLD,
                      borderRadius: "12px",
                      onClick: async () => {
                        await addServiceProviderCost(dialogData);
                      },
                      disabled: isSaveDisabled || (dialogMode === "edit" ? !hasJwtPermission("PUT_SERVICE_PROVIDER_COST") : !hasJwtPermission("POST_SERVICE_PROVIDER_COST")),
                    }
                  : {
                      label: "Delete",
                      variant: "contained",
                      bgColor: COLORS.GOLD,
                      textColor: COLORS.iconColor,
                      borderRadius: "12px",
                      onClick: deleteServiceProviderCost,
                      disabled: !hasJwtPermission("DELETE_SERVICE_PROVIDER_COST"),
                    },
              ]
        }
      />
    );
  }
);

export default ServiceProviderCostPage;
